import { Stack } from "@mui/material";
import Box from "@mui/material/Box";
import { Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import React from "react";
import ButtonBar from "../../components/Buttons/ButtonBar";
import NewButton from "../../components/Buttons/NewButton";
import UploadButton from "../../components/Buttons/UploadButton";
import { Title } from "../../components/Desc";
import ProgressIndicator from "../../components/ProgressIndicator";
import BasicMenu from "../../components/menu/BasicMenu";
import strings from "../../config/localization";
import { useGetAllDesignationsQuery } from "../../services/appln";
import { doesPermissionExist } from "../util";
import DeleteDesignation from "./DeleteDesignation";
import EditDesignation from "./EditDesignation";
import NewDesignation from "./NewDesignation";
import UploadDesignations from "./UploadDesignations";

interface Designation {
  key: string;
  name: string;
  serialNo: string;
  abbreviation: string;
}

interface Data {
  rowData: Designation[];
  columnData: ColumnsType<Designation>;
}

export default function ListDesignations() {
  const [newDialogOpen, setNewDialogOpen] = React.useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = React.useState(false);

  const [data, setData] = React.useState<Data>({
    rowData: [],
    columnData: [],
  });

  const getParams = {
    page: 1,
    limit: 1000,
    search: "",
  };

  const designationsQueryResponse = useGetAllDesignationsQuery(getParams);

  React.useEffect(() => {
    if (!designationsQueryResponse.isSuccess) {
      return;
    }

    const rowData = designationsQueryResponse.data.data.map<Designation>(
      (e: any) => {
        return {
          key: e.id,
          name: e.name,
          serialNo: e.serialNo,
          abbreviation: e.abbreviation,
        };
      },
    );

    const columns: ColumnsType<Designation> = [
      {
        title: strings.serialNo,
        dataIndex: "serialNo",
        key: "serialNo",
        width: 5,
      },
      {
        title: strings.name,
        dataIndex: "name",
        key: "name",
        width: 20,
      },
      {
        title: strings.abbreviation,
        dataIndex: "abbreviation",
        key: "abbreviation",
        width: 5,
      },
      {
        title: "",
        dataIndex: "edit",
        key: "edit",
        width: 2,
        render: (text, record, index) =>
          doesPermissionExist("update-designation") && (
            <EditDesignation id={record.key} initialValues={record} />
          ),
      },
      {
        title: "",
        dataIndex: "delete",
        key: "delete",
        width: 2,
        render: (text, record, index) =>
          doesPermissionExist("delete-designation") && (
            <DeleteDesignation id={record.key} />
          ),
      },
    ];

    setData({
      rowData: rowData,
      columnData: columns,
    });
  }, [designationsQueryResponse]);

  return (
    <div>
      <Box sx={{ height: 400, width: "100%" }}>
        <Stack spacing={2} style={{ alignItems: "flex-start" }}>
          <Title text={strings.designations} />
          {(designationsQueryResponse.isLoading ||
            designationsQueryResponse.isFetching) && <ProgressIndicator />}
          {doesPermissionExist("create-designation") && (
            <ButtonBar>
              <NewButton
                onClickHandler={() => {
                  setNewDialogOpen(true);
                }}
              />
              <NewDesignation
                onSubmitHandler={(newObj: any) => {
                  setNewDialogOpen(false);
                }}
                onCancelHandler={() => {
                  setNewDialogOpen(false);
                }}
                state={newDialogOpen}
              />
              <UploadButton
                onClickHandler={() => {
                  setUploadDialogOpen(true);
                }}
              />
              <UploadDesignations
                state={uploadDialogOpen}
                onSubmitHandler={(newObj: any) => {
                  setUploadDialogOpen(false);
                }}
                onCancelHandler={() => {
                  setUploadDialogOpen(false);
                }}
              />
            </ButtonBar>
          )}
          <Table
            virtual
            scroll={{ y: 600 }}
            columns={data.columnData}
            dataSource={data.rowData}
            pagination={false}
          />
        </Stack>
      </Box>
    </div>
  );
}
